import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instrument: '',
    level: '',
    preferredDay: '',
    preferredTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create form data for FormSubmit
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('phone', formData.phone);
      form.append('instrument', formData.instrument);
      form.append('level', formData.level);
      form.append('preferredDay', formData.preferredDay);
      form.append('preferredTime', formData.preferredTime);
      form.append('message', formData.message);
      form.append('_subject', `New Lesson Booking Request from ${formData.name}`);
      form.append('_template', 'box');
      form.append('_captcha', 'false');
      
      const response = await fetch('https://formsubmit.co/felixaz@live.com', {
        method: 'POST',
        body: form
      });
      
      if (response.ok) {
        toast.success('Booking request submitted! I\'ll contact you soon to confirm.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          instrument: '',
          level: '',
          preferredDay: '',
          preferredTime: '',
          message: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Book Your Lesson
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Take the first step in your musical journey. Fill out the form below and I'll get back to you within 24 hours.
          </p>
        </div>
        
        <Card className="bg-slate-900/80 border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Lesson Booking Form</CardTitle>
            <CardDescription className="text-slate-400">
              Share your details and preferences, and I'll schedule your first lesson
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Full Name *
                  </Label>
                  <Input 
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="bg-slate-900 border-slate-700 text-white focus:border-blue-500"
                    placeholder="John Doe"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="bg-slate-900 border-slate-700 text-white focus:border-blue-500"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number *
                </Label>
                <Input 
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  className="bg-slate-900 border-slate-700 text-white focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              {/* Lesson Details */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="instrument" className="text-slate-300">
                    Instrument *
                  </Label>
                  <Select value={formData.instrument} onValueChange={(value) => handleChange('instrument', value)}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white focus:border-blue-500">
                      <SelectValue placeholder="Select instrument" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="piano" className="text-slate-300 focus:bg-slate-800 focus:text-white">Piano</SelectItem>
                      <SelectItem value="drums" className="text-slate-300 focus:bg-slate-800 focus:text-white">Drums</SelectItem>
                      <SelectItem value="percussion" className="text-slate-300 focus:bg-slate-800 focus:text-white">Percussion</SelectItem>
                      <SelectItem value="marching-drums" className="text-slate-300 focus:bg-slate-800 focus:text-white">Marching Drums</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="level" className="text-slate-300">
                    Skill Level *
                  </Label>
                  <Select value={formData.level} onValueChange={(value) => handleChange('level', value)}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white focus:border-blue-500">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="beginner" className="text-slate-300 focus:bg-slate-800 focus:text-white">Beginner</SelectItem>
                      <SelectItem value="intermediate" className="text-slate-300 focus:bg-slate-800 focus:text-white">Intermediate</SelectItem>
                      <SelectItem value="advanced" className="text-slate-300 focus:bg-slate-800 focus:text-white">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Scheduling */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="day" className="text-slate-300 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Preferred Day of Week *
                  </Label>
                  <Select value={formData.preferredDay} onValueChange={(value) => handleChange('preferredDay', value)}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white focus:border-blue-500">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="monday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Monday</SelectItem>
                      <SelectItem value="tuesday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Tuesday</SelectItem>
                      <SelectItem value="wednesday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Wednesday</SelectItem>
                      <SelectItem value="thursday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Thursday</SelectItem>
                      <SelectItem value="friday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Friday</SelectItem>
                      <SelectItem value="saturday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Saturday</SelectItem>
                      <SelectItem value="sunday" className="text-slate-300 focus:bg-slate-800 focus:text-white">Sunday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-slate-300 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Preferred Time *
                  </Label>
                  <Select value={formData.preferredTime} onValueChange={(value) => handleChange('preferredTime', value)}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white focus:border-blue-500">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="morning" className="text-slate-300 focus:bg-slate-800 focus:text-white">Morning (9AM - 12PM)</SelectItem>
                      <SelectItem value="afternoon" className="text-slate-300 focus:bg-slate-800 focus:text-white">Afternoon (12PM - 5PM)</SelectItem>
                      <SelectItem value="evening" className="text-slate-300 focus:bg-slate-800 focus:text-white">Evening (5PM - 8PM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-300 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Additional Information
                </Label>
                <Textarea 
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white focus:border-blue-500 min-h-32"
                  placeholder="Tell us about your musical goals, any previous experience, or special requirements..."
                />
              </div>
            </form>
          </CardContent>
          
          <CardFooter>
            <Button 
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-lg py-6 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </section>
  );
};

export default Booking;